import { useEffect } from 'react';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { usdText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { MutationError } from '../../shared/ui/mutation-error';
import { SectionPanel } from '../../shared/ui/section-panel';
import type { TAiBudgetToday } from '../../entities/organization';
import type { TAiBudget } from './use-ai-budget';

const CENTS = /^\$?\d+(\.\d{1,2})?$/;

const schemaFor = (ceiling: number) =>
  z.object({
    aiDailyBudgetUsd: z
      .string()
      .trim()
      .regex(CENTS, 'Dollars and whole cents, like 20 or 12.50. 0 turns AI steps off.')
      .refine((value) => Number(value.replace('$', '')) <= ceiling, {
        message: `At most ${usdText(ceiling)}, the ceiling of this installation.`,
      }),
  });

type TBudgetValues = { aiDailyBudgetUsd: string };

const plain = (usd: number) => usd.toFixed(2);

interface IBudgetCardProps {
  today: TAiBudgetToday;
  budget: TAiBudget;
  editable: boolean;
  /** Bumped by "Raise today's budget": the field takes focus. */
  focusSignal: number;
}

export const BudgetCard = ({ today, budget, editable, focusSignal }: IBudgetCardProps) => {
  const form = useApiForm<TBudgetValues>({
    schema: schemaFor(today.ceilingUsd),
    defaultValues: { aiDailyBudgetUsd: plain(today.configuredUsd ?? today.defaultUsd) },
    onSubmit: (values) => budget.save.mutateAsync(Number(values.aiDailyBudgetUsd.replace('$', ''))),
  });
  const unset = today.configuredUsd === null;

  useEffect(() => {
    if (focusSignal > 0) form.form.setFocus('aiDailyBudgetUsd', { shouldSelect: true });
  }, [focusSignal, form.form]);

  return (
    <SectionPanel title="Daily budget" note="US dollars">
      <div className="px-card py-4">
        {editable ? (
          <ApiForm api={form} flush>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Field<TBudgetValues> name="aiDailyBudgetUsd" label="Budget per day">
                  <TextInput mono inputMode="decimal" autoComplete="off" />
                </Field>
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    waiting={form.form.formState.isSubmitting}
                    waitingLabel="Saving"
                  >
                    Save
                  </Button>
                  {!unset && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={budget.save.isPending}
                      onClick={() =>
                        budget.save.mutate(null, {
                          onSuccess: () =>
                            form.form.reset({ aiDailyBudgetUsd: plain(today.defaultUsd) }),
                        })
                      }
                    >
                      Use the default, {usdText(today.defaultUsd)}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-data leading-20 text-muted">
                Whole cents, up to {usdText(today.ceilingUsd)}. $0 turns AI steps off. Raising it
                today lets held work go on at once; the new amount applies from tomorrow too.
                {unset ? ` Not set, so the default of ${usdText(today.defaultUsd)} applies.` : ''}
              </p>
            </div>
          </ApiForm>
        ) : (
          <p className="text-data text-muted">
            The budget is {usdText(today.limitUsd)} a day. Only an owner or an admin can change it.
          </p>
        )}
        <MutationError error={budget.save.variables === null ? budget.save.error : null} />
      </div>
    </SectionPanel>
  );
};
